// TermGuardSection — 社内用語ガード debug panel (iter_240 + iter_241).
//
// Shows the loaded company dictionary and lets an admin paste arbitrary
// text to see exactly what the guard detects / normalizes. Also lists the
// most recent correction logs. iter_241 adds a top-of-panel alerts banner
// for terms that the guard has corrected 3+ times inside a rolling 7-day
// window — the operator should investigate the source prompt.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, TestTube, Book, ClipboardList, AlertTriangle, Check } from 'lucide-react';
import { jstDateTime } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai/term-guard`;

const TYPE_BADGE = {
  company:            'bg-indigo-50 text-indigo-700 border-indigo-200',
  department:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  system:             'bg-sky-50 text-sky-700 border-sky-200',
  system_feature:     'bg-teal-50 text-teal-700 border-teal-200',
  machine_or_project: 'bg-amber-50 text-amber-700 border-amber-200',
  project:            'bg-amber-50 text-amber-700 border-amber-200',
  employee:           'bg-violet-50 text-violet-700 border-violet-200',
};

const SOURCE_BADGE = {
  mfg_departments:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  planner_projects: 'bg-amber-50 text-amber-700 border-amber-200',
  users:            'bg-violet-50 text-violet-700 border-violet-200',
};

const SAMPLE = 'meeting with seizo about sensbey web app traceability and SS45BMC';

export default function TermGuardSection() {
  const [dict, setDict] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [text, setText] = useState(SAMPLE);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const loadAlerts = useCallback(() => {
    axios.get(`${API}/alerts?only_open=true`, { withCredentials: true })
      .then((r) => setAlerts(r.data || [])).catch(() => setAlerts([]));
  }, []);

  useEffect(() => {
    axios.get(`${API}/dictionary`, { withCredentials: true })
      .then((r) => setDict(r.data)).catch(() => setDict({}));
    axios.get(`${API}/logs?limit=25`, { withCredentials: true })
      .then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
    loadAlerts();
  }, [loadAlerts]);

  const ackAlert = async (forbidden) => {
    try {
      await axios.post(`${API}/alerts/${encodeURIComponent(forbidden)}/ack`, {},
        { withCredentials: true });
      toast.success('確認済みにしました');
      loadAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '確認に失敗しました');
    }
  };

  const runCheck = async () => {
    if (!text.trim()) { toast.error('テキストを入力してください'); return; }
    setChecking(true);
    try {
      const r = await axios.post(`${API}/check`, { text }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'チェックに失敗しました');
    } finally { setChecking(false); }
  };

  return (
    <div className="space-y-4" data-testid="term-guard-section">
      <div className="rounded-xl border border-indigo-100 bg-white p-4">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} strokeWidth={1.6} className="text-indigo-600" />
          <h3 className="text-base font-bold text-indigo-900">社内用語ガード</h3>
          <span className="ml-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5">Global</span>
        </div>
        <p className="text-[12px] text-zinc-600 leading-relaxed">
          すべてのAI機能（日報 / チャット / ダイジェスト / ナレッジ / トレーサビリティ提案 / AIインポート 等）は、
          LLMへ送信する前に社内用語を正規化し、応答後に禁止語を修正します。ここではその動作を検証できます。
        </p>
      </div>

      {/* Alerts banner — highest priority, only if any open alerts */}
      {alerts.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="term-guard-alerts">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} strokeWidth={2} className="text-amber-700" />
            <span className="text-sm font-bold text-amber-900">
              補正が繰り返されている用語があります（要対応 {alerts.length}件）
            </span>
          </div>
          <p className="text-[11px] text-amber-800 leading-snug">
            AI応答で同じ禁止語が過去7日間に3回以上補正されました。ソースプロンプトまたは辞書の更新をご検討ください。
          </p>
          <div className="space-y-1.5">
            {alerts.map((a) => (
              <div key={a.forbidden} data-testid={`term-guard-alert-${a.forbidden}`}
                className="flex items-center gap-2 rounded-md bg-white border border-amber-200 px-2.5 py-1.5">
                <span className="font-mono text-[12px] text-rose-700 line-through">{a.forbidden}</span>
                <span className="text-zinc-400">→</span>
                <span className="font-mono text-[12px] text-emerald-700">{a.canonical}</span>
                <span className="ml-auto text-[10px] text-zinc-500 font-mono">
                  {a.count}回 · {a.last_feature} · {jstDateTime(a.last_seen)}
                </span>
                <button type="button" onClick={() => ackAlert(a.forbidden)}
                  data-testid={`term-guard-alert-ack-${a.forbidden}`}
                  className="h-7 px-2 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 inline-flex items-center gap-1">
                  <Check size={11} strokeWidth={2.5} /> 確認
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tester */}
      <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="term-guard-tester">
        <h4 className="flex items-center gap-1.5 text-sm font-bold text-zinc-800 mb-2">
          <TestTube size={14} strokeWidth={1.7} className="text-indigo-500" /> テキスト検証
        </h4>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          data-testid="term-guard-text"
          className="w-full rounded-lg border border-zinc-200 p-2 text-[12px] font-mono resize-y focus:outline-none focus:ring-2 focus:ring-indigo-200"
        />
        <div className="mt-2 flex items-center gap-2">
          <button type="button" onClick={runCheck} disabled={checking}
            data-testid="term-guard-run"
            className="h-9 px-3 rounded-lg bg-indigo-600 text-white text-xs font-semibold shadow-sm hover:bg-indigo-700 disabled:opacity-50">
            {checking ? '検査中…' : '検査する'}
          </button>
          <button type="button" onClick={() => setText(SAMPLE)}
            className="text-[11px] text-zinc-500 underline hover:text-zinc-700">
            サンプルを貼り付け
          </button>
        </div>
        {result && (
          <div className="mt-3 space-y-2" data-testid="term-guard-result">
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-2 text-[12px]">
              <div className="text-[10px] font-bold text-zinc-500 uppercase mb-1">正規化後</div>
              <div className="font-mono text-zinc-800" data-testid="term-guard-normalized">{result.normalized_text}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase mb-1">検出された用語 ({result.detected_terms.length})</div>
              <div className="space-y-1">
                {result.detected_terms.length === 0 && <div className="text-[11px] text-zinc-400">検出された社内用語はありません</div>}
                {result.detected_terms.map((d, i) => (
                  <div key={i} data-testid={`term-guard-hit-${i}`}
                    className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 text-[12px] ${d.ambiguous ? 'border-amber-300 bg-amber-50/60' : 'border-zinc-200 bg-white'}`}>
                    <span className="font-mono text-zinc-500">{d.input}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="font-semibold text-zinc-800">{d.canonical}</span>
                    <span className={`px-1.5 py-0 text-[10px] font-semibold rounded-full border ${TYPE_BADGE[d.type] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>
                      {d.type || 'unknown'}
                    </span>
                    {d.source && d.source !== 'company_dictionary' && (
                      <span className={`px-1.5 py-0 text-[10px] font-semibold rounded-full border ${SOURCE_BADGE[d.source] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}
                        title="Live DB record">
                        DB: {d.source}
                      </span>
                    )}
                    {d.ambiguous && (
                      <span className="px-1.5 py-0 text-[10px] font-semibold rounded-full border bg-amber-100 text-amber-800 border-amber-300 inline-flex items-center gap-1"
                        title={d.ambiguous.join(', ')}>
                        <AlertTriangle size={10} /> 曖昧
                      </span>
                    )}
                    <span className="ml-auto text-[10px] text-zinc-400 font-mono">{Math.round((d.confidence || 0) * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
            {result.warnings?.length > 0 && (
              <ul className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 space-y-0.5" data-testid="term-guard-warnings">
                {result.warnings.map((w, i) => (
                  <li key={i} className="flex gap-1.5">
                    <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* Dictionary */}
      <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="term-guard-dict">
        <h4 className="flex items-center gap-1.5 text-sm font-bold text-zinc-800 mb-2">
          <Book size={14} strokeWidth={1.7} className="text-indigo-500" /> 社内用語辞書 ({dict ? Object.keys(dict).length : 0}件)
        </h4>
        <div className="text-[11px] text-zinc-500 mb-2">
          編集は <code className="font-mono text-[10px] bg-zinc-100 px-1 py-0.5 rounded">backend/config/company_dictionary.json</code> を書き換えてください（再起動不要 · 変更は自動反映）。
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {dict && Object.entries(dict).map(([canonical, meta]) => (
            <div key={canonical} className="rounded-lg border border-zinc-100 bg-zinc-50/40 p-2" data-testid={`term-guard-entry-${meta.type}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[13px] font-bold text-zinc-800">{canonical}</span>
                <span className="text-[10px] text-zinc-500 font-mono">/ {meta.canonical_en}</span>
                <span className={`ml-auto px-1.5 py-0 text-[10px] font-semibold rounded-full border ${TYPE_BADGE[meta.type] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>
                  {meta.type}
                </span>
              </div>
              {meta.notes && <div className="text-[11px] text-zinc-500 mb-1 italic">{meta.notes}</div>}
              <div className="flex flex-wrap gap-1 mb-1">
                {(meta.aliases || []).map((a, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-[10px] font-mono text-zinc-600">{a}</span>
                ))}
              </div>
              {(meta.forbidden_outputs || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <span className="text-[10px] font-bold text-rose-600 mr-1">禁止:</span>
                  {(meta.forbidden_outputs || []).map((f, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-[10px] font-mono text-rose-700 line-through">{f}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Correction logs */}
      <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="term-guard-logs">
        <h4 className="flex items-center gap-1.5 text-sm font-bold text-zinc-800 mb-2">
          <ClipboardList size={14} strokeWidth={1.7} className="text-indigo-500" /> 直近の補正ログ ({logs.length})
        </h4>
        {logs.length === 0 ? (
          <div className="text-[11px] text-zinc-400 italic">まだ記録がありません（AI機能が用語を修正した際にここに表示されます）</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {logs.map((l, i) => (
              <div key={l.id || i} className="rounded-md border border-zinc-100 bg-zinc-50/40 p-2 text-[11px]" data-testid={`term-guard-log-${i}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-mono text-zinc-400">{jstDateTime(l.created_at, true)}</span>
                  <span className="font-semibold text-zinc-700">{l.feature_name}</span>
                  {l.model_name && <span className="text-zinc-400 font-mono">/ {l.model_name}</span>}
                  {l.modified && <span className="ml-auto px-1.5 py-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold">補正あり</span>}
                </div>
                {(l.corrections || []).map((c, j) => (
                  <div key={j} className="flex gap-2 font-mono text-[10px]">
                    <span className="text-rose-600 line-through">{c.from}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="text-emerald-700">{c.to}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
